import {
  COST_IMAGE_1K,
  COST_IMAGE_2K,
  COST_IMAGE_4K,
  COST_FAST_1K,
  COST_FAST_2K,
  COST_FAST_4K,
} from "./imagePricing.js";
/** Provider-independent image settings, shared by UI, queue and backend. */
export type ImageModel =
  "nano-banana-pro" | "nano-banana-fast" | "gpt-image-2.5-sunburst";
export type ImageResolution = "1K" | "2K" | "4K";
export interface ImageGenerationSettings {
  model: ImageModel;
  resolution: ImageResolution;
  /** Omitted or original preserves the action's input framing. */
  aspectRatio?: string;
  customSize?: { width: number; height: number };
}
export const SUNBURST_MODEL: ImageModel = "gpt-image-2.5-sunburst";
export const IMAGE_ASPECT_RATIOS = [
  "1:1",
  "4:3",
  "3:2",
  "5:4",
  "3:4",
  "2:3",
  "4:5",
  "16:9",
  "21:9",
  "9:16",
] as const;
export const DEFAULT_IMAGE_GENERATION: ImageGenerationSettings = {
  model: "nano-banana-pro",
  resolution: "1K",
  aspectRatio: "original",
};
export const MAX_CUSTOM_EDGE = 4096;
export const MAX_SUNBURST_PIXELS = 8_294_400;
export const MIN_SUNBURST_PIXELS = 655_360;

/**
 * Normalize persisted model identifiers.
 * @param {unknown} value Legacy model value.
 * @return {ImageModel} Supported model.
 */
export function normalizeImageModel(value: unknown): ImageModel {
  if (value === "gpt-image-2" || value === SUNBURST_MODEL)
    return SUNBURST_MODEL;
  if (
    value === "nano-banana-fast" ||
    value === "gemini-nano-banana-2.1" ||
    value === "gemini-3.1-flash-image" ||
    value === "standard" ||
    value === "fast"
  )
    return "nano-banana-fast";
  return "nano-banana-pro";
}

/**
 * Validate a settings snapshot before submitting.
 * @param {ImageGenerationSettings} settings Selected settings.
 * @return {string | undefined} Validation code, if invalid.
 */
export function imageGenerationError(
  settings: ImageGenerationSettings
): string | undefined {
  if (
    !["nano-banana-pro", "nano-banana-fast", SUNBURST_MODEL].includes(
      settings.model
    )
  )
    return "model";
  if (!["1K", "2K", "4K"].includes(settings.resolution)) return "resolution";
  if (
    settings.customSize !== undefined &&
    (!settings.customSize ||
      typeof settings.customSize !== "object" ||
      Array.isArray(settings.customSize))
  )
    return "custom_size";
  if (
    settings.aspectRatio !== undefined &&
    typeof settings.aspectRatio !== "string"
  )
    return "aspect_ratio";
  if (settings.customSize) {
    if (settings.model !== SUNBURST_MODEL) return "custom_model";
    const { width, height } = settings.customSize;
    if (
      ![width, height].every(
        (n) => Number.isInteger(n) && n >= 64 && n <= MAX_CUSTOM_EDGE
      )
    )
      return "custom_size";
    if (Math.max(width / height, height / width) > 3) return "custom_ratio";
  } else if (
    settings.aspectRatio &&
    settings.aspectRatio !== "original" &&
    !(IMAGE_ASPECT_RATIOS as readonly string[]).includes(settings.aspectRatio)
  )
    return "aspect_ratio";
  return undefined;
}

/**
 * Calculate delivered pixels for presets or custom output.
 * @param {ImageGenerationSettings} settings Selected settings.
 * @param {number} sourceRatio Input width divided by height.
 * @return {object} Delivered width and height.
 */
export function imageOutputSize(
  settings: ImageGenerationSettings,
  sourceRatio = 1
) {
  if (settings.customSize) return { ...settings.customSize };
  const ratioParts = settings.aspectRatio?.split(":").map(Number);
  const ratio =
    ratioParts?.length === 2 ? ratioParts[0] / ratioParts[1] : sourceRatio;
  const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  const edge =
    settings.resolution === "4K"
      ? 4096
      : settings.resolution === "2K"
        ? 2048
        : 1024;
  // Rounding must not turn a valid 3:1 source into an invalid 1024:341 canvas.
  const minShortEdge =
    safeRatio >= 1 / 3 && safeRatio <= 3 ? Math.ceil(edge / 3) : 1;
  return safeRatio >= 1
    ? {
        width: edge,
        height: Math.max(minShortEdge, Math.round(edge / safeRatio)),
      }
    : {
        width: Math.max(minShortEdge, Math.round(edge * safeRatio)),
        height: edge,
      };
}

/**
 * Fit output inside fal native pixel and aspect limits.
 * @param {object} target Delivered width and height.
 * @return {object} Native width and height in 16px steps.
 */
export function sunburstNativeSize(target: { width: number; height: number }) {
  const ratio = target.width / target.height;
  if (!Number.isFinite(ratio) || ratio < 1 / 3 || ratio > 3)
    throw new Error("custom_ratio");
  const pixels = target.width * target.height;
  const scale = Math.min(
    3840 / Math.max(target.width, target.height),
    Math.sqrt(MAX_SUNBURST_PIXELS / pixels),
    Math.max(1, Math.sqrt(MIN_SUNBURST_PIXELS / pixels))
  );
  let width = Math.max(16, Math.ceil((target.width * scale) / 16) * 16);
  let height = Math.max(16, Math.ceil((target.height * scale) / 16) * 16);
  if (width > 3840 || height > 3840 || width * height > MAX_SUNBURST_PIXELS) {
    width = Math.max(16, Math.floor((target.width * scale) / 16) * 16);
    height = Math.max(16, Math.floor((target.height * scale) / 16) * 16);
  }
  // Round small outputs up to the minimum area without exceeding the ratio cap.
  while (width * height < MIN_SUNBURST_PIXELS) {
    if (width / height < ratio) width += 16;
    else height += 16;
  }
  if (width > height * 3) height = Math.ceil(width / 3 / 16) * 16;
  if (height > width * 3) width = Math.ceil(height / 3 / 16) * 16;
  return { width, height };
}

/**
 * Classify custom output into customer resolution tiers.
 * @param {ImageGenerationSettings} settings Selected settings.
 * @return {ImageResolution} Delivered resolution tier.
 */
export function imageResolutionForSettings(
  settings: ImageGenerationSettings
): ImageResolution {
  if (!settings.customSize) return settings.resolution;
  const edge = Math.max(settings.customSize.width, settings.customSize.height);
  return edge <= 1024 ? "1K" : edge <= 2048 ? "2K" : "4K";
}

/**
 * Price the delivered resolution, retaining Nano Fast tiers.
 * @param {ImageGenerationSettings} settings Selected settings.
 * @return {number} Customer credits.
 */
export function imageGenerationCredits(
  settings: ImageGenerationSettings
): number {
  const resolution = imageResolutionForSettings(settings);
  return (
    settings.model === "nano-banana-fast"
      ? { "1K": COST_FAST_1K, "2K": COST_FAST_2K, "4K": COST_FAST_4K }
      : { "1K": COST_IMAGE_1K, "2K": COST_IMAGE_2K, "4K": COST_IMAGE_4K }
  )[resolution];
}

/**
 * Switch the model. Custom pixel sizes exist only for ChatGPT Image, so
 * leaving it falls back to the default format instead of stranding the
 * selection on an option the new model cannot generate. The resolution tier
 * follows the custom size, which keeps the quoted cost where it was.
 * @param {ImageGenerationSettings} settings Current settings.
 * @param {ImageModel} model Newly selected model.
 * @return {ImageGenerationSettings} Settings valid for the new model.
 */
export function withImageModel(
  settings: ImageGenerationSettings,
  model: ImageModel
): ImageGenerationSettings {
  if (model === SUNBURST_MODEL || !settings.customSize) {
    return { ...settings, model };
  }
  return {
    ...settings,
    model,
    resolution: imageResolutionForSettings(settings),
    aspectRatio: DEFAULT_IMAGE_GENERATION.aspectRatio,
    customSize: undefined,
  };
}

/** Validate mode-specific choices before submitting or charging.
 * @param {ImageGenerationSettings} settings Selected settings.
 * @param {object} constraints Action requirements.
 * @return {string | undefined} Translated validation code.
 */
export function imageGenerationActionError(
  settings: ImageGenerationSettings,
  constraints: { requireAspectRatio?: boolean; requiresSunburst?: boolean } = {}
): string | undefined {
  return (
    imageGenerationError(settings) ||
    (constraints.requiresSunburst && settings.model !== SUNBURST_MODEL
      ? "grain_model"
      : undefined) ||
    (constraints.requireAspectRatio &&
    !settings.customSize &&
    (!settings.aspectRatio || settings.aspectRatio === "original")
      ? "target_required"
      : undefined)
  );
}

/** Actions that edit the existing canvas rather than choose a new format.
 * @param {string} mode API, workflow or Studio action name.
 * @return {boolean} Whether the source framing is fixed.
 */
export function preservesImageFormat(mode: string): boolean {
  return ["recolor", "add_avatar", "Re-Coloring", "Add Avatar"].includes(mode);
}

/** Keep the delivery tier while dropping an unrelated previous format choice.
 * @param {ImageGenerationSettings} settings Current selection.
 * @return {ImageGenerationSettings} Selection for editing the original canvas.
 */
export function withOriginalImageFormat(
  settings: ImageGenerationSettings
): ImageGenerationSettings {
  return {
    ...settings,
    resolution: imageResolutionForSettings(settings),
    aspectRatio: "original",
    customSize: undefined,
  };
}

/** Authoritative metadata shared by persisted records and immediate results.
 * @param {Record<string, unknown>} saved Mode-specific and legacy metadata.
 * @param {string | undefined} model Actual provider model.
 * @param {ImageGenerationSettings | undefined} settings Effective selection.
 * @return {Record<string, unknown>} Normalized metadata.
 */
export function normalizeImageGenerationMetadata(
  saved: Record<string, unknown>,
  model?: string,
  settings?: ImageGenerationSettings
): Record<string, unknown> {
  const merged: Record<string, unknown> = {
    ...saved,
    ...(model ? { model } : {}),
  };
  if (!settings) return merged;
  if (!settings.customSize) {
    delete merged.width;
    delete merged.height;
  }
  return {
    ...merged,
    imageGeneration: settings,
    resolution: imageResolutionForSettings(settings),
    ...(settings.customSize
      ? {
          width: settings.customSize.width,
          height: settings.customSize.height,
          aspectRatio: `${settings.customSize.width}:${settings.customSize.height}`,
        }
      : settings.aspectRatio && settings.aspectRatio !== "original"
        ? { aspectRatio: settings.aspectRatio }
        : {}),
  };
}
